import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Plus,
  Clock,
  FileText,
  CheckCircle,
  AlertCircle,
  TrendingUp,
  ArrowRight,
  MoreHorizontal,
  BarChart3,
  Calendar
} from 'lucide-react';
import { Button } from "../../../foundation/altzero_base/base/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../../../foundation/altzero_base/base/components/ui/card";
import { Badge } from "../../../foundation/altzero_base/base/components/ui/badge";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "../../../foundation/altzero_base/base/components/ui/dropdown-menu";
import ScopingAILayout from '../components/ScopingAILayout';
import {
  getUserTenders,
  TenderSummary,
  deleteTender
} from '../services/tenderWorkflowService';
import { getDashboardAnalytics, DashboardAnalytics } from "../services/dashboardService";
import { usePermissions } from "../../../foundation/altzero_base/base/contextapi/PermissionsContext";
import { Chart as ChartJS, CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend, ArcElement } from 'chart.js';
import { Bar, Doughnut } from 'react-chartjs-2';

ChartJS.register(CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend, ArcElement);

const Dashboard: React.FC = () => {
  const navigate = useNavigate();
  const { can } = usePermissions();
  const [tenders, setTenders] = useState<TenderSummary[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [analytics, setAnalytics] = useState<DashboardAnalytics | null>(null);

  useEffect(() => {
    loadDashboardData();
  }, []);

  const loadDashboardData = async (silent = false) => {
    if (silent) setIsRefreshing(true);
    else setIsLoading(true);
    try {
      // Run tenders fetch and analytics in parallel
      const [tendersResult, analyticsResult] = await Promise.all([
        getUserTenders(),
        getDashboardAnalytics(),
      ]);

      if (tendersResult.success && tendersResult.data) {
        setTenders(tendersResult.data);
      }
      if (analyticsResult.success && analyticsResult.data) {
        setAnalytics(analyticsResult.data);
      }
    } catch (error) {
      console.error('[Dashboard] Failed to load dashboard data:', error);
    } finally {
      setIsLoading(false);
      setIsRefreshing(false);
    }
  };

  const handleCreateNew = () => {
    navigate('/tender/new-proposal');
  };

  const handleResumeTender = (tenderId: string, status?: string) => {
    const normalizedStatus = status?.toLowerCase().trim();
    if (normalizedStatus === 'tender_ready') {
      navigate(`/tender/details/${tenderId}`);
    } else {
      navigate(`/tender/documents/new?tender=${tenderId}`);
    }
  };

  const handleDeleteTender = async (tenderId: string) => {
    if (window.confirm('Are you sure you want to delete this tender? This action cannot be undone.')) {
      const result = await deleteTender(tenderId);
      if (result.success) {
        loadDashboardData();
      }
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'draft':
        return 'bg-gray-100 text-gray-800';
      case 'waiting_for_retailers_reply':
        return 'bg-yellow-100 text-yellow-800';
      case 'retailers_replied':
        return 'bg-purple-100 text-purple-800';
      case 'sent_to_client':
        return 'bg-blue-100 text-blue-800';
      case 'under_review':
        return 'bg-orange-100 text-orange-800';
      case 'tender_ready':
        return 'bg-green-100 text-green-800';
      case 'in_progress':
        return 'bg-blue-100 text-blue-800';
      case 'review':
        return 'bg-yellow-100 text-yellow-800';
      case 'approved':
        return 'bg-green-100 text-green-800';
      case 'rejected':
        return 'bg-red-100 text-red-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const getStatusLabel = (status: string) => {
    const labels: Record<string, string> = {
      'draft': 'Draft',
      'waiting_for_retailers_reply': 'Awaiting Replies',
      'retailers_replied': 'Retailers Replied',
      'sent_to_client': 'Sent to Client',
      'under_review': 'Under Review',
      'tender_ready': 'Tender Ready',
      'in_progress': 'In Progress',
      'review': 'Review',
      'approved': 'Approved',
      'rejected': 'Rejected',
    };
    return labels[status] || status.replace('_', ' ').toUpperCase();
  };

  const getStepLabel = (step: number) => {
    const steps = [
      'Client & Sites',
      'Retailer Communication',
      'Offers',
      'Compare & Select',
      'Recommendation',
      'Template',
      'Offer Details',
      'Overview'
    ];
    return steps[step - 1] || `Step ${step}`;
  };

  return (
    <ScopingAILayout>
      <div className="p-8 space-y-8 w-full">
        {/* Header */}
        <div className="flex justify-between items-center">
          <div>
            <h1 className="text-3xl font-bold text-gray-900 tracking-tight">Dashboard</h1>
            <p className="text-gray-500 mt-1">Welcome back! Here's what's happening with your tenders.</p>
          </div>
          <div className="flex gap-2">
            <Button variant="outline" onClick={() => loadDashboardData(true)} disabled={isLoading || isRefreshing}>
              <Clock className={`w-4 h-4 mr-2 ${isRefreshing ? 'animate-spin' : ''}`} />
              {isRefreshing ? 'Refreshing...' : 'Refresh'}
            </Button>
            {can('tender.create') && (
              <Button onClick={handleCreateNew} className="gap-2 shadow-lg shadow-primary/20 hover:shadow-primary/30 transition-all">
                <Plus className="w-4 h-4" />
                New Tender
              </Button>
            )}
          </div>
        </div>

        {/* Analytics Section */}
        {isLoading && !analytics ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {[1, 2, 3, 4, 5, 6].map((i) => (
              <Card key={i} className="bg-white border-none shadow-sm">
                <CardContent className="p-6">
                  <div className="animate-pulse">
                    <div className="h-4 bg-gray-200 rounded w-24 mb-3"></div>
                    <div className="h-8 bg-gray-200 rounded w-16"></div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        ) : analytics && (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            <Card className="bg-white border-none shadow-sm hover:shadow-md transition-shadow">
              <CardContent className="p-6 flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-500">Total Pipeline</p>
                  <h3 className="text-2xl font-bold text-gray-900 mt-1">{analytics.metrics.pipelineValue}</h3>
                </div>
                <div className="p-3 bg-blue-50 rounded-full">
                  <Clock className="w-5 h-5 text-blue-600" />
                </div>
              </CardContent>
            </Card>
            <Card className="bg-white border-none shadow-sm hover:shadow-md transition-shadow">
              <CardContent className="p-6 flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-500">Submitted</p>
                  <h3 className="text-2xl font-bold text-gray-900 mt-1">{analytics.metrics.totalSubmitted}</h3>
                </div>
                <div className="p-3 bg-purple-50 rounded-full">
                  <TrendingUp className="w-5 h-5 text-purple-600" />
                </div>
              </CardContent>
            </Card>
            <Card className="bg-white border-none shadow-sm hover:shadow-md transition-shadow">
              <CardContent className="p-6 flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-500">Completed</p>
                  <h3 className="text-2xl font-bold text-gray-900 mt-1">{analytics.metrics.completedThisMonth}</h3>
                </div>
                <div className="p-3 bg-green-50 rounded-full">
                  <CheckCircle className="w-5 h-5 text-green-600" />
                </div>
              </CardContent>
            </Card>
            <Card className="bg-white border-none shadow-sm hover:shadow-md transition-shadow">
              <CardContent className="p-6 flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-500">Booked</p>
                  <h3 className="text-2xl font-bold text-gray-900 mt-1">{analytics.metrics.bookedThisMonth}</h3>
                </div>
                <div className="p-3 bg-orange-50 rounded-full">
                  <BarChart3 className="w-5 h-5 text-orange-600" />
                </div>
              </CardContent>
            </Card>

            {/* New Email Stats Cards */}
            <Card className="bg-white border-none shadow-sm hover:shadow-md transition-shadow">
              <CardContent className="p-6 flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-500">Emails Sent</p>
                  <h3 className="text-2xl font-bold text-gray-900 mt-1">
                    {tenders.reduce((sum, t) => sum + (t.retailers_sent_count || 0), 0)}
                  </h3>
                  <p className="text-xs text-gray-400 mt-1">To retailers</p>
                </div>
                <div className="p-3 bg-indigo-50 rounded-full">
                  <TrendingUp className="w-5 h-5 text-indigo-600" />
                </div>
              </CardContent>
            </Card>
            <Card className="bg-white border-none shadow-sm hover:shadow-md transition-shadow">
              <CardContent className="p-6 flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-500">Replies Received</p>
                  <h3 className="text-2xl font-bold text-gray-900 mt-1">
                    {tenders.reduce((sum, t) => sum + (t.retailers_replied_count || 0), 0)}
                  </h3>
                  <p className="text-xs text-gray-400 mt-1">From retailers</p>
                </div>
                <div className="p-3 bg-teal-50 rounded-full">
                  <CheckCircle className="w-5 h-5 text-teal-600" />
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Charts Section */}
        {isLoading && !analytics ? (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {[1, 2].map(i => (
              <Card key={i} className="bg-white border-none shadow-sm animate-pulse">
                <CardHeader>
                  <div className="h-4 bg-gray-200 rounded w-36" />
                </CardHeader>
                <CardContent>
                  <div className="h-[300px] bg-gray-100 rounded-lg" />
                </CardContent>
              </Card>
            ))}
          </div>
        ) : analytics && (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <Card className="bg-white border-none shadow-sm">
              <CardHeader>
                <CardTitle className="text-lg font-semibold text-gray-900">Tenders by Month</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="h-[300px] w-full">
                  <Bar
                    data={{
                      labels: analytics.tendersByMonth.map(d => d.name),
                      datasets: [
                        {
                          label: 'Tenders',
                          data: analytics.tendersByMonth.map(d => d.value),
                          backgroundColor: 'rgba(59, 130, 246, 0.8)',
                          borderRadius: 4,
                        },
                      ],
                    }}
                    options={{
                      responsive: true,
                      maintainAspectRatio: false,
                      plugins: {
                        legend: {
                          display: false,
                        },
                      },
                      scales: {
                        y: {
                          beginAtZero: true,
                          ticks: {
                            stepSize: 1,
                          },
                        },
                      },
                    }}
                  />
                </div>
              </CardContent>
            </Card>

            <Card className="bg-white border-none shadow-sm">
              <CardHeader>
                <CardTitle className="text-lg font-semibold text-gray-900">Tenders by Status</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="h-[300px] w-full flex items-center justify-center">
                  <div className="w-[300px]">
                    <Doughnut
                      data={{
                        labels: analytics.tendersByStatus.map(d => d.name.charAt(0).toUpperCase() + d.name.slice(1)),
                        datasets: [
                          {
                            data: analytics.tendersByStatus.map(d => d.value),
                            backgroundColor: [
                              'rgba(59, 130, 246, 0.8)',   // Blue
                              'rgba(16, 185, 129, 0.8)',   // Green
                              'rgba(245, 158, 11, 0.8)',   // Amber
                              'rgba(239, 68, 68, 0.8)',    // Red
                              'rgba(107, 114, 128, 0.8)',  // Gray
                            ],
                            borderWidth: 0,
                          },
                        ],
                      }}
                      options={{
                        responsive: true,
                        maintainAspectRatio: true,
                        plugins: {
                          legend: {
                            position: 'bottom',
                          },
                        },
                      }}
                    />
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Recent Tenders */}
        <div className="space-y-4">
          <h2 className="text-xl font-semibold text-gray-900 flex items-center gap-2">
            <FileText className="w-5 h-5 text-primary" />
            Recent Tenders
          </h2>

          {isLoading && tenders.length === 0 ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {[1, 2, 3].map((i) => (
                <Card key={i} className="h-48 animate-pulse bg-gray-50 border-none" />
              ))}
            </div>
          ) : tenders.length === 0 ? (
            <Card className="bg-gray-50 border-dashed border-2 border-gray-200">
              <CardContent className="flex flex-col items-center justify-center py-12 text-center">
                <div className="w-12 h-12 bg-gray-100 rounded-full flex items-center justify-center mb-4">
                  <FileText className="w-6 h-6 text-gray-400" />
                </div>
                <h3 className="text-lg font-medium text-gray-900">No tenders yet</h3>
                <p className="text-gray-500 mt-1 mb-4 max-w-sm">
                  Get started by creating your first tender proposal. It only takes a few minutes.
                </p>
                {can('tender.create') && <Button onClick={handleCreateNew}>Create Tender</Button>}
              </CardContent>
            </Card>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {tenders.slice(0, 3).map((tender) => (
                <Card
                  key={tender.id}
                  className="group hover:shadow-lg transition-all duration-300 border-gray-200 hover:border-primary/20 cursor-pointer relative overflow-hidden"
                  onClick={() => handleResumeTender(tender.id, tender.status)}
                >
                  <div className="absolute top-0 left-0 w-1 h-full bg-primary/0 group-hover:bg-primary transition-all duration-300" />
                  <CardHeader className="pb-3">
                    <div className="flex justify-between items-start">
                      <Badge className={`${getStatusColor(tender.status)} border-none`}>
                        {getStatusLabel(tender.status)}
                      </Badge>
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button variant="ghost" className="h-8 w-8 p-0 opacity-0 group-hover:opacity-100 transition-opacity" onClick={(e) => e.stopPropagation()}>
                            <MoreHorizontal className="h-4 w-4" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          <DropdownMenuLabel>Actions</DropdownMenuLabel>
                          <DropdownMenuItem onClick={(e) => {
                            e.stopPropagation();
                            handleResumeTender(tender.id, tender.status);
                          }}>
                            Resume Tender
                          </DropdownMenuItem>
                          {can('tender.delete') && (
                            <>
                              <DropdownMenuSeparator />
                              <DropdownMenuItem
                                className="text-red-600 focus:text-red-600"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleDeleteTender(tender.id);
                                }}
                              >
                                Delete
                              </DropdownMenuItem>
                            </>
                          )}
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </div>
                    <CardTitle className="text-lg font-semibold text-gray-900 line-clamp-1 mt-2">
                      {tender.opportunity_name}
                    </CardTitle>
                    <p className="text-sm text-gray-500 flex items-center gap-1 flex-wrap">
                      <span className="font-medium text-gray-700">{tender.client_name || 'Unknown Client'}</span>
                      {tender.site_count && (
                        <>
                          <span>•</span>
                          <span>{tender.site_count} Sites</span>
                        </>
                      )}
                      {(tender.retailers_sent_count || 0) > 0 && (
                        <>
                          <span>•</span>
                          <span className="text-blue-600 font-medium">
                            {tender.retailers_replied_count || 0}/{tender.retailers_sent_count || 0} Replied
                          </span>
                        </>
                      )}
                    </p>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-4">
                      <div className="space-y-2">
                        <div className="flex justify-between text-xs text-gray-500">
                          <span>Progress</span>
                          <span>{tender.status === 'tender_ready' ? 100 : tender.progress_percentage}%</span>
                        </div>
                        <div className="h-2 bg-gray-100 rounded-full overflow-hidden">
                          <div
                            className="h-full bg-primary transition-all duration-500 ease-out"
                            style={{ width: `${tender.status === 'tender_ready' ? 100 : tender.progress_percentage}%` }}
                          />
                        </div>
                      </div>

                      {(tender.retailers_sent_count || 0) > 0 && (
                        <div className="pt-2 border-t border-gray-50">
                          <div className="flex items-center justify-between text-xs">
                            <span className="text-gray-500">Retailer Response</span>
                            <span className="font-medium text-blue-600">
                              {tender.retailers_replied_count || 0}/{tender.retailers_sent_count || 0}
                            </span>
                          </div>
                        </div>
                      )}
                      <div className="flex items-center justify-between text-xs text-gray-500 pt-2 border-t border-gray-50">
                        <div className="flex items-center gap-1">
                          <Clock className="w-3 h-3" />
                          <span>{new Date(tender.last_accessed_at).toLocaleDateString()}</span>
                        </div>
                        <div className="font-medium text-primary">
                          {getStepLabel(tender.current_step)}
                        </div>
                      </div>

                      <Button
                        className="w-full gap-2 mt-2 bg-gray-900 hover:bg-primary text-white shadow-sm transition-all"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleResumeTender(tender.id, tender.status);
                        }}
                      >
                        Continue
                        <ArrowRight className="w-4 h-4" />
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </div>
      </div>
    </ScopingAILayout>
  );
};

export default Dashboard;
